import type { Copy } from '../types/copy'

export const en: Copy = {
  nav: {
    services: 'Services',
    gallery: 'Gallery',
    about: 'About Us',
    process: 'Process',
    contact: 'Contact',
    quote: 'Get Free Quote',
    callUs: 'Call us',
    menu: 'Open menu',
    close: 'Close menu',
    language: 'Language',
  },
  hero: {
    eyebrow: 'Make Your Home Reality',
    headline: 'Complete Home Construction &',
    headlineAccent: 'Repair Solutions',
    sub: 'From roofing to granite to professional painting, we handle all your home improvement needs with expert craftsmanship.',
    badges: ['Licensed & Insured', '20+ Years Experience', 'Free Estimates'],
    primary: 'Get Free Quote',
    secondary: 'View Our Work',
    serving: 'Serving San Ysidro, CA',
    locationLine: 'Serving San Ysidro, California | Licensed & Insured',
    callNow: 'Call now',
  },
  trust: {
    items: [
      { id: 'years', value: '20+', label: 'Years Across All Home Services' },
      { id: 'projects', value: '1000+', label: 'Projects Completed' },
      { id: 'inhouse', value: 'In-House', label: 'All Core Work, No Subs' },
      { id: 'licensed', value: 'Lic. 1126459', label: 'Licensed & Insured' },
    ],
    note: "San Ysidro's trusted choice. Same crew, same quality, from a leaking roof to a waterfall granite island.",
  },
  services: {
    title: 'Our Services',
    subtitle:
      'From roofing repairs to professional painting to premium granite counters, we handle all your home improvement needs. Full-service construction and renovation expertise.',
    learnMore: 'Learn More',
    items: [
      {
        id: 'granite-countertops',
        name: 'Granite Countertops',
        description:
          'Premium granite selection, precision templating, fabrication and installation for kitchens, baths and bars.',
      },
      {
        id: 'kitchen-remodeling',
        name: 'Kitchen Remodeling',
        description:
          'Complete renovations with custom cabinetry, granite counters, new layouts, lighting and finish carpentry.',
      },
      {
        id: 'bathroom-design',
        name: 'Bathroom Design',
        description:
          'Modern bathrooms with granite vanities, tiled showers and fixtures, waterproofed and built to code.',
      },
      {
        id: 'roofing',
        name: 'Roofing Services',
        description:
          'Roof repair, replacement and new installation for residential and commercial, including flashing and ventilation.',
      },
      {
        id: 'painting',
        name: 'Professional Painting Services',
        description:
          'Interior and exterior painting with premium paints and expert application — accent walls, full interior refreshes and commercial exteriors. Color consultation, proper surface preparation and drywall patching included for a flawless finish.',
      },
      {
        id: 'drywall-interior',
        name: 'Drywall & Interior',
        description:
          'Drywall installation, patching, taping, texture and finishing, plus interior wall framing and repair.',
      },
      {
        id: 'wood-rot-repair',
        name: 'Wood Rot Repair',
        description:
          'Structural wood rot treatment, fascia and trim replacement, restoration with treated, sealed lumber.',
      },
      {
        id: 'tile-flooring',
        name: 'Tile & Flooring',
        description:
          'Premium tile, natural stone and hardwood flooring installation, including underlayment and heated floors.',
      },
      {
        id: 'custom-cabinetry',
        name: 'Custom Cabinetry',
        description:
          'Hand-crafted cabinets designed and built for your space, with soft-close hardware and durable finishes.',
      },
      {
        id: 'handyman',
        name: 'General Handyman',
        description:
          'Maintenance, repairs and small construction projects handled in one visit by an experienced crew.',
      },
    ],
  },
  why: {
    eyebrow: 'About Us',
    title: 'Why Choose Pollo Construction & Granite?',
    intro:
      'Two decades of experience serving San Ysidro and Southern California homes, one crew, and every core trade in-house. Whatever the job, the same people who quoted it are the people who finish it.',
    local:
      'From San Ysidro kitchens to complete home renovations throughout South Bay, Pollo Construction has earned the trust of hundreds of homeowners with quality workmanship and personalized service.',
    highlight: 'Complete Home Solutions',
    highlightBody: 'From small repairs to major renovations.',
    points: [
      {
        id: 'craft',
        title: 'Expert Craftsmanship',
        description:
          'Licensed professionals with 20+ years of experience across every home service we offer.',
      },
      {
        id: 'diverse',
        title: 'Diverse Expertise',
        description:
          'Not just granite — roofing, professional painting, drywall, wood rot, tile, cabinetry, handyman work and more.',
      },
      {
        id: 'materials',
        title: 'Quality Materials',
        description:
          'Premium materials on every project, installed to manufacturer standards and local code.',
      },
      {
        id: 'warranty',
        title: 'Complete Warranty',
        description:
          'Satisfaction guarantee and written workmanship warranty on all services we perform.',
      },
    ],
    cta: 'Schedule Your Free Consultation',
  },
  portfolio: {
    title: 'Service Portfolio',
    subtitle:
      'A look at the range of work we finish every week in San Ysidro and South Bay communities.',
    filters: [
      { id: 'all', label: 'All Services' },
      { id: 'roofing', label: 'Roofing' },
      { id: 'painting', label: 'Painting' },
      { id: 'drywall', label: 'Drywall' },
      { id: 'woodrot', label: 'Wood Rot' },
      { id: 'granite', label: 'Granite' },
      { id: 'bathrooms', label: 'Bathrooms' },
      { id: 'kitchens', label: 'Kitchens' },
    ],
    items: [
      {
        id: 'roof-install',
        title: 'Architectural Shingle Roof',
        service: 'Roofing Services',
        category: 'roofing',
        caption: 'Full tear-off, new underlayment, ridge vent and flashing.',
      },
      {
        id: 'accent-wall',
        title: 'Modern Accent Wall',
        service: 'Professional Painting',
        category: 'painting',
        caption: 'Color consultation, full surface prep and razor-sharp cut lines.',
      },
      {
        id: 'interior-refresh',
        title: 'Complete Interior Refresh',
        service: 'Professional Painting',
        category: 'painting',
        caption: 'Multiple rooms, coordinated colors and a flawless finish throughout.',
      },
      {
        id: 'exterior-repaint',
        title: 'Exterior House Repaint',
        service: 'Professional Painting',
        category: 'painting',
        caption: 'Pressure wash, prime and two coats of weather-resistant exterior paint.',
      },
      {
        id: 'drywall-finish',
        title: 'Smooth-Finish Interior',
        service: 'Drywall & Interior',
        category: 'drywall',
        caption: 'New board, taped, floated and painted to a level 5 finish.',
      },
      {
        id: 'fascia-renewal',
        title: 'Fascia & Trim Renewal',
        service: 'Wood Rot Repair',
        category: 'woodrot',
        caption: 'Rotted fascia replaced with treated lumber, sealed and painted.',
      },
      {
        id: 'granite-kitchen',
        title: 'Waterfall Granite Island',
        service: 'Granite Countertops',
        category: 'granite',
        caption: 'Absolute Noir with a mitered waterfall edge and full backsplash.',
      },
      {
        id: 'absolute-noir-slab',
        title: 'Absolute Noir Slab',
        service: 'Granite Countertops',
        category: 'granite',
        caption: 'Deep uniform black with fine silver flecks. Polished, 3 cm.',
      },
      {
        id: 'copper-canyon-slab',
        title: 'Copper Canyon Slab',
        service: 'Granite Countertops',
        category: 'granite',
        caption: 'Exotic copper and blue-gray movement. No two slabs alike.',
      },
      {
        id: 'spa-bathroom',
        title: 'Spa Bathroom Build',
        service: 'Bathroom Design',
        category: 'bathrooms',
        caption: 'Granite double vanity, frameless glass and large-format tile.',
      },
      {
        id: 'open-kitchen',
        title: 'Open-Concept Kitchen',
        service: 'Kitchen Remodeling',
        category: 'kitchens',
        caption: 'Wall removal, structural beam and custom two-tone cabinetry.',
      },
      {
        id: 'built-in-cabinetry',
        title: 'Built-In Living Wall',
        service: 'Custom Cabinetry',
        category: 'kitchens',
        caption: 'Shop-built shaker cabinetry with integrated LED lighting.',
      },
    ],
    cta: 'Request Your Free Estimate',
    empty: 'No projects in this category yet — call us and ask, we have probably done it.',
  },
  granite: {
    items: [
      {
        id: 'absolute-noir',
        name: 'Absolute Noir',
        category: 'black',
        origin: 'India',
        finish: 'Polished',
        thickness: '3 cm',
        description: 'Deep uniform black with fine silver flecks.',
      },
      {
        id: 'silver-cloud',
        name: 'Silver Cloud',
        category: 'gray',
        origin: 'Brazil',
        finish: 'Polished',
        thickness: '3 cm',
        description: 'Creamy white base with soft gray veining.',
      },
      {
        id: 'steel-grey',
        name: 'Steel Grey',
        category: 'gray',
        origin: 'India',
        finish: 'Polished or Honed',
        thickness: '2 or 3 cm',
        description: 'Cool mid-gray with linear movement.',
      },
      {
        id: 'cafe-imperial',
        name: 'Café Imperial',
        category: 'brown',
        origin: 'Brazil',
        finish: 'Polished',
        thickness: '3 cm',
        description: 'Warm coffee and caramel tones with golden speckles.',
      },
      {
        id: 'golden-sand',
        name: 'Golden Sand',
        category: 'brown',
        origin: 'Brazil',
        finish: 'Polished',
        thickness: '3 cm',
        description: 'Honey and sand tones with dense mineral detail.',
      },
      {
        id: 'copper-canyon',
        name: 'Copper Canyon',
        category: 'multi',
        origin: 'Brazil',
        finish: 'Polished',
        thickness: '3 cm',
        description: 'Exotic swirls of copper, cream and blue-gray.',
      },
    ],
    origin: 'Origin',
    finish: 'Finish',
    thickness: 'Thickness',
    quoteThis: 'Get a quote on this',
  },
  projects: {
    title: 'Our Work in San Ysidro',
    subtitle: 'Transforming homes with quality craftsmanship and attention to detail.',
    filters: [
      { id: 'all', label: 'All Services' },
      { id: 'roofing', label: 'Roofing' },
      { id: 'painting', label: 'Painting' },
      { id: 'granite', label: 'Granite' },
      { id: 'bathrooms', label: 'Bathrooms' },
      { id: 'handyman', label: 'Handyman' },
    ],
    before: 'Before',
    after: 'After',
    dragHint: 'Drag to compare',
    viewDetails: 'View Project Details',
    ctaTitle: 'Ready to Transform Your Home?',
    ctaSub: 'Get a free estimate for your San Ysidro home project.',
    ctaConsult: 'Schedule Free Consultation',
    ctaCall: 'Call: (805) 310-6530',
    ctaServices: 'View Our Services',
    items: [
      {
        id: 'roof-san-ysidro',
        name: 'Storm Damage Roof Restoration',
        location: 'San Ysidro, CA',
        service: 'Roofing Contractor',
        category: 'roofing',
        description:
          'Storm damage roof restoration in San Ysidro, CA. Complete tear-off and replacement with premium architectural shingles, new flashing, vents and ridge caps. 10-year workmanship warranty.',
        detail:
          'Storms lifted and cracked shingles across the north slope. We tore the roof down to the deck, replaced damaged sheathing, installed synthetic underlayment, new valley and chimney flashing, ridge ventilation and premium architectural shingles. Finished in four days with a full nail sweep of the property and a 10-year workmanship warranty.',
      },
      {
        id: 'painting-san-ysidro',
        name: 'Modern Accent Wall',
        location: 'San Ysidro, CA',
        service: 'Professional Painting',
        category: 'painting',
        description:
          'Bold accent wall transformation. Professional color consultation, surface prep, and expert application for a modern, stylish interior.',
        detail:
          'The wall came to us scuffed and full of nail holes. We patched and sanded every blemish, primed the repairs, masked the trim and ceiling, then rolled two coats of premium low-VOC interior paint. Crisp hand-cut lines at the ceiling, trim and adjacent walls, finished in a single day.',
      },
      {
        id: 'granite-san-ysidro',
        name: 'Premium Granite Kitchen Install',
        location: 'San Ysidro, CA',
        service: 'Granite Countertops',
        category: 'granite',
        description:
          'Absolute Noir granite counters with mitered waterfall island edge and new backsplash.',
        detail:
          'Dated tops removed and cabinet runs re-leveled, then templated the same day. The island was fabricated as a single mitered waterfall so the veining wraps the corner. Undermount sink, eased edges and full backsplash completed in nine working days.',
      },
      {
        id: 'bathroom-san-ysidro',
        name: 'Modern Bathroom Renovation',
        location: 'San Ysidro, CA',
        service: 'Bathroom Design',
        category: 'bathrooms',
        description:
          'Full bathroom renovation including flooring, vanity, fixtures, and custom tile shower.',
        detail:
          'Demo to the studs, new waterproofing and relocated plumbing for a double vanity. Steel Grey granite vanity top, large-format porcelain flooring, custom tiled shower with frameless glass, and matte black fixtures throughout.',
      },
      {
        id: 'handyman-san-ysidro',
        name: 'Interior Updates & Repairs',
        location: 'San Ysidro, CA',
        service: 'General Handyman',
        category: 'handyman',
        description:
          'Complete home maintenance including repairs, updates, and improvements throughout.',
        detail:
          'A single punch-list visit: patched and repainted scuffed walls, re-set loose door casing, replaced worn baseboards, swapped dated light fixtures, adjusted sticking doors and re-caulked wet areas. Two days, one crew, one invoice.',
      },
    ],
  },
  process: {
    eyebrow: 'How it works',
    title: 'Our Process',
    subtitle: 'Five clear steps for any service, and one person accountable the whole way.',
    steps: [
      {
        id: 'consultation',
        name: 'Consultation',
        description: 'Free in-home estimate for any service, with a response within 48 hours.',
      },
      {
        id: 'assessment',
        name: 'Assessment',
        description: 'Professional evaluation of the work and detailed, written project planning.',
      },
      {
        id: 'materials',
        name: 'Material Selection',
        description: 'Choose your finishes, materials and design options with our guidance.',
      },
      {
        id: 'construction',
        name: 'Construction',
        description: 'Expert installation and skilled craftsmanship by our own in-house crew.',
      },
      {
        id: 'inspection',
        name: 'Inspection',
        description: 'Final walkthrough, quality assurance and our satisfaction guarantee.',
      },
    ],
    note: 'For painting projects, we handle all surface preparation including patching, sanding and priming to ensure professional results — and color consultation is part of the first visit.',
    cta: 'Start With a Free Estimate',
  },
  testimonials: {
    title: 'What Our Customers Say',
    subtitle: 'Real projects across every service we offer.',
    items: [
      {
        id: 'maria-l',
        quote:
          'They templated our counters on a Monday and installed by Thursday. The seam on the island is nearly invisible and the crew cleaned up better than they found it.',
        name: 'Maria L.',
        meta: 'San Ysidro, CA • Granite Countertops',
      },
      {
        id: 'carlos-r',
        quote:
          'Después de la tormenta nos quedamos sin la mitad del techo. Llegaron al día siguiente, cubrieron todo y en cuatro días teníamos techo nuevo. Gente honesta y muy trabajadora.',
        name: 'Carlos R.',
        meta: 'San Ysidro, CA • Roof Replacement',
      },
      {
        id: 'david-h',
        quote:
          'They gutted our main bathroom and put it back better than the showroom photos. Same crew every single day, and the tile work in the shower is flawless.',
        name: 'David H.',
        meta: 'Chula Vista, CA • Bathroom Renovation',
      },
      {
        id: 'james-m',
        quote:
          'I had a list of a dozen small repairs no one else wanted to touch. They knocked out every item in two days and charged exactly what they quoted.',
        name: 'James M.',
        meta: 'Imperial Beach, CA • General Handyman',
      },
    ],
    prev: 'Previous testimonial',
    next: 'Next testimonial',
    goTo: 'Go to testimonial',
  },
  care: {
    title: 'Home Maintenance Tips',
    subtitle: 'Simple habits that protect your home — and your investment — between projects.',
    items: [
      {
        id: 'roof',
        title: 'Roof Maintenance',
        description:
          'Inspect once a year and after major storms. Keep gutters clear, trim overhanging branches, and replace lifted shingles early before water reaches the deck.',
      },
      {
        id: 'drywall',
        title: 'Drywall Care',
        description:
          'Most drywall failures start with moisture. Run bath fans for 20 minutes after showers, fix small leaks fast, and repaint high-humidity rooms with a moisture-resistant paint.',
      },
      {
        id: 'wood',
        title: 'Wood Protection',
        description:
          'Keep exterior trim, fascia and decks sealed. Re-caulk seams every few years and check anywhere gutters overflow — that is where rot always begins.',
      },
      {
        id: 'sealing',
        title: 'Granite Sealing',
        description:
          'Most granite should be sealed every 12 to 18 months. Drop water on the surface: if it darkens within 5 minutes, it is time to reseal.',
      },
      {
        id: 'cleaning',
        title: 'Surface Cleaning',
        description:
          'Warm water, mild dish soap and a soft cloth is all you need daily. Skip vinegar, bleach and citrus cleaners, which dull the polish on stone.',
      },
      {
        id: 'prevention',
        title: 'Damage Prevention',
        description:
          'Use trivets under hot pans, cutting boards for knives, and wipe up wine, oil and coffee quickly to avoid staining unsealed spots.',
      },
      {
        id: 'paint',
        title: 'Paint Care & Protection',
        description:
          'Quality paint protects your home. Indoors, check for peeling once a year and repaint high-moisture rooms like baths and kitchens with moisture-resistant paint. Outdoors, watch for fading and chalking and repaint every 5 to 7 years depending on exposure. Keep gutters and drainage clear so water never sits against painted surfaces.',
      },
    ],
    cta: 'Download Care Guide (PDF)',
  },
  cta: {
    title: "Let's Make Your Home Reality",
    sub: 'Contact us today for a free consultation and a written estimate on any service.',
    call: 'Call (805) 310-6530',
    quote: 'Get Free Quote',
  },
  contact: {
    title: 'Request Your Free Quote',
    subtitle:
      'Tell us about your project anywhere in the San Ysidro area and we will call you back the same business day.',
    formTitle: 'Project details',
    name: 'Full name',
    phone: 'Phone',
    email: 'Email',
    service: 'Service interested in',
    servicePlaceholder: 'Select a service',
    serviceOther: 'Other (please describe)',
    granite: 'Granite selection (optional)',
    granitePlaceholder: 'Not applicable / not sure yet',
    message: 'Project description',
    messagePlaceholder: 'Scope, timeline, and anything else we should know…',
    submit: 'Request Free Quote',
    submitting: 'Sending…',
    privacy:
      'We use your information only to contact you about your project. No spam, no sharing with third parties.',
    successTitle: 'Request received',
    successBody:
      'Thanks! We have your project details and will call you back the same business day. For anything urgent, call (805) 310-6530.',
    successAgain: 'Send another request',
    errors: {
      name: 'Please enter your name.',
      phone: 'Please enter a valid 10-digit phone number.',
      email: 'Please enter a valid email address.',
      service: 'Please choose a service.',
      message: 'Please tell us a little about the project (10+ characters).',
    },
    infoTitle: 'Contact information',
    areaLabel: 'Service area',
    areaValue: 'San Ysidro, California',
    phoneLabel: 'Phone',
    emailLabel: 'Email',
    licenseLabel: 'License',
    hoursLabel: 'Hours',
    hours: ['Monday – Friday: 7:00 AM – 6:00 PM', 'Saturday: 8:00 AM – 2:00 PM', 'Sunday: Closed'],
    mapLabel: 'Map of our California service area',
    followLabel: 'Follow us',
  },
  footer: {
    about:
      'Licensed home construction contractor serving San Ysidro, California and surrounding South Bay communities. Complete renovation specialist with family ownership, expert crew, and mastery in roofing, professional painting, drywall, wood repair, granite, and all home services.',
    serviceArea: 'Service area: San Ysidro, CA | License: Lic. 1126459',
    proudly:
      'Proudly serving San Ysidro, California with professional home construction, renovation and repair services.',
    quickLinks: 'Quick links',
    contact: 'Contact',
    follow: 'Follow us',
    privacy: 'Privacy Policy',
    terms: 'Terms',
    rights: 'All rights reserved.',
    licensed: 'Licensed & Insured',
  },
}
